import { styled } from '@/styles/stitches.config';

type TermsOfUseEnProps = {
  title: string;
};

const Section = styled('section', {
  marginBlock: '$8',
});

const H1 = styled('h1', {
  color: '$white',
  marginBlockEnd: '$8',
});

const H2 = styled('h2', {
  color: '$white',
  fontWeight: '$normal',
  fontSize: '$3',
});

const StyledA = styled('a', {
  color: '$white',
});

type AProps = {
  href: string;
  children: React.ReactNode;
};

const A = (props: AProps) => {
  return (
    <StyledA href={props.href} rel="noopener noreferrer" target={'_blank'}>
      {props.children}
    </StyledA>
  );
};

export function TermsOfUseEn(props: TermsOfUseEnProps) {
  const { title } = props;

  return (
    <>
      <H1>{title}</H1>

      <Section>
        <H2>1. User Agreement</H2>
        <p>
          The <i>Changing Childhood Project</i> website hosts materials related to the United
          Nations Children’s Fund (UNICEF’s) mandate. In particular, the site contains results of a
          global poll that was conducted by UNICEF together with its partners. The topic of the poll
          is childhood in the 21st century. The available materials include but are not limited to
          data, visualizations, animations, videos, articles, and editorials. The materials are
          meant to expand knowledge and understanding on certain subjects. The contents of the site
          are only for your personal use only and not for commercial use.
        </p>
        <p>
          Please read these Terms of Use carefully. These Terms of Use constitute an agreement
          between you and UNICEF. By continuing to use this site, you agree to have read,
          understood, and accepted the terms and conditions contained below. If you do not agree to
          any of these terms and conditions, please do not continue to use this site.
        </p>
        <p>
          These Terms of Use are subject to change without notice, so please make sure you check
          this page for any updates. The date of the most recent revisions will appear at the bottom
          of this page. If you continue to use this site, this means that you accept any changes to
          or revisions of these Terms of Use.
        </p>
        <p>
          As used in these Terms of Use, “we,” “us,” and “our” refers to UNICEF. The terms “you,”
          “your,” “yours” and “user” refer to you as the individual or organizing accessing or using
          this site.
        </p>
      </Section>

      <Section>
        <H2>2. Site Content, Rules of Usage</H2>
        <p>
          The site is open to and welcomes people from around the world. UNICEF aims to create an
          environment on the <i>Changing Childhood Project</i> website where every person feels
          comfortable, no matter their nationality, gender, cultural background, religion, sexual
          orientation or political conviction.
        </p>
        <p>
          You agree to use this site only for lawful purposes, and in a manner that does not
          infringe the rights of or restrict or inhibit the use and enjoyment of this site by, any
          third party.
        </p>
        <p>
          UNICEF owns and retains all rights in and to the site’s source code, the design,
          functionality, and architecture ofthe site, and any software, support materials or content
          provided through this site (collectively the{' '}
          <i>“Changing Childhood Project website IP”</i>). Except for any rights explicitly granted
          under these Terms of Use, you are not granted any rights in and to any{' '}
          <i>Changing Childhood Project</i> website IP. If you want to use{' '}
          <i>Changing Childhood Project</i> website IP in a way that is not allowed by these Terms
          of Use, you must first contact the <i>Changing Childhood Project</i> website team to
          obtain written permission.
        </p>
        <p>
          Unless explicitly noted, all materials provided by the UNICEF on this site are licensed
          under the Creative Commons Attribution-Noncommercial-No Derivatives 4.0 Unported license.
          This license allows you and others to copy and redistribute the content on the site, but
          it does not allow you or others to remix or transform the content or use the content for
          commercial purposes. This license requires you to attribute any material you use to the
          original author. When you use <strong>any of site’s materials</strong>, please use the
          following attribution: “A survey conducted by UNICEF and Gallup. See{' '}
          <A href="https://creativecommons.org/faq/">Creative Commons FAQ page</A>, where you will
          find more information.”
        </p>
        <p>
          Please note that this site and any support materials may contain images, sounds and videos
          that are trademarked by third parties. Nothing in these Terms of Use or the Creative
          Commons license limits or reduces a third party’s full intellectual property rights. You
          may not use these materials to label, promote, or endorse any product or service. You are
          solely responsible for any violation of a third party’s intellectual property rights
          caused by your misuse of these materials. You agree to comply with all applicable laws and
          regulations in your country when you use this site.
        </p>
        <p>
          The <i>Changing Childhood Project</i> and UNICEF names and logos are the exclusive
          property of UNICEF. They are protected under international and national laws. Unauthorized
          use is prohibited. They may not be copied or reproduced in any way without the prior
          written permission of UNICEF. Requests for permission should be sent to{' '}
          <A href="mailto:nyhqdoc.permit@unicef.org">nyhqdoc.permit@unicef.org</A>
          All other trademarks, service marks and logos used on this site, with or without
          attribution, are the trademarks, service marks or logos of their respective owners.{' '}
        </p>
        <p>
          UNICEF reserves the right to seek all remedies available by law for any violation of these
          Terms of Use, including any violation of UNICEF's rights in the UNICEF name and emblem and
          its rights in connection with the information, artwork, text, video, audio, pictures or
          other content of the site.
        </p>
      </Section>

      <Section>
        <H2>3. Privacy, Cookies and Information Disclosure</H2>
        <p>
          Your privacy is important to us. Please refer to our{' '}
          <A href="https://www.unicef.org/legal#privacy-policy">Privacy Policy</A>, which will
          include information on what and how UNICEF collects, stores, and uses limited user
          information. Please also be aware that by continuing to use this site you are confirming
          that you have read and accept our{' '}
          <A href="https://www.unicef.org/legal#privacy-policy">Privacy Policy</A>.
        </p>
        <p>
          UNICEF uses cookies on this site to track its usage. Please refer to{' '}
          <A href="https://www.unicef.org/legal#unicef-cookies-policy">UNICEF cookies policy</A> for
          details on how cookies are being used.
        </p>
        <p>
          UNICEF is committed to making information about its programmes and operations available to
          the public. Please refer to{' '}
          <A href="https://www.unicef.org/legal#information-disclosure-policy">
            UNICEF Information Disclosure Policy
          </A>{' '}
          for details.
        </p>
      </Section>

      <Section>
        <H2>4. Links to Other Sites</H2>
        <p>
          The site may include links to other websites that are not under UNICEF's control. UNICEF
          provides these links merely as a convenience and the inclusion of such links does not
          imply an endorsement or approval by UNICEF of any website, product or service. If you
          decide to access linked third-party websites or applications, you do so at your own risk.
          UNICEF does not assume any responsibility or liability in respect of such websites,
          including, for example, responsibility or liability for the accuracy or reliability of any
          information, data, opinions, advice or statements made on those websites.
        </p>
      </Section>

      <Section>
        <H2>5. Disclaimer </H2>
        <p>
          All content that the UNICEF provides on this site is provided “as is.” This means we
          cannot guarantee that all content on this site is completely accurate or error-free.{' '}
        </p>
        <p>
          The United Nations and UNICEF disclaim any liability or responsibility arising from the
          use of this site or its content. The United Nations, including UNICEF, its officials,
          employees, consultants, agents and contractors, will not be liable for any financial or
          other consequences whatsoever arising from the use of this site and its content, including
          the inappropriate, improper, or fraudulent use of such content.
        </p>
        <p>
          UNICEF accepts no responsibility or liability in respect of the conduct of any user in
          connection with this site, or for any material submitted by users and carried on this
          site, including, for example, responsibility or liability for the accuracy or reliability
          of any information, data, opinions, advice or statements made in such material.
        </p>
        <p>
          UNICEF does not warrant that the functions contained in the content or resources on this
          site will be uninterrupted or error free, that defects will be corrected, or that this
          site or the server that makes it available are free of viruses or represent the full
          functionality, accuracy, reliability of the content or resources. No representations or
          warranties of any kind concerning this site are given, including responsibility for any
          infection by virus or any other contamination or by anything which has destructive
          properties.
        </p>
        <p>
          You agree to indemnify, at your own expense, UNICEF, its officials, employees, consultants
          and agents, against any claims, including their costs and expenses, by any third party,
          relating to or arising out of (a) your use or attempted use of this site, resources or
          services in violation of this agreement; (b) your violation of any law or rights of any
          third party; and (c) any user content that you post or otherwise make available on this
          site or through any service, including without limitation any claim of infringement or
          misappropriation of intellectual property or other proprietary rights.
        </p>
        <p>
          Except as otherwise expressly provided in these Terms of Use, UNICEF does not make any
          representation or extends any warranty of any kind, either express or implied, to the user
          with respect to any of the services. For example, we don’t make any commitments about the
          content within the services, the specific functions of the services, or their reliability,
          availability, or ability to meet your needs. UNICEF hereby disclaims all implied
          warranties of merchantability, fitness for particular purpose and noninfringement with
          respect to any and all of the foregoing.
        </p>
        <p>
          UNICEF periodically adds, changes, improves or updates the materials on this site without
          notice. Under no circumstances shall UNICEF be liable for any loss, damage, liability or
          expense incurred or suffered that is claimed to have resulted from the use of this site,
          including, without limitation, any fault, error, omission, interruption or delay with
          respect thereto. The use of this site is at the user’s sole risk. Under no circumstances,
          including but not limited to negligence, shall UNICEF or its affiliates be liable for any
          damages, even if UNICEF has been advised of the possibility of such damages. Make sure you
          use content on this site thoughtfully and responsibly.
        </p>
        <p>
          You agree that you are solely responsible for any issues that you may encounter that
          result from your use of this site. Under no circumstances can UNICEF be held responsible
          for any damages or injuries you may have related to your use of this site.
        </p>
        <p>
          The mention of names of specific companies or products on this site does not imply any
          intention to infringe proprietary rights, nor should it be interpreted as an endorsement
          or recommendation on the part of UNICEF. The use of particular designations of countries
          or territories or maps on this site does not reflect a position by UNICEF on the legal
          status of such countries or territories, of their authorities and institutions or of the
          delimitation of their boundaries.
        </p>
        <p>
          The use of particular designations of countries or territories or maps on this site does
          not reflect a position by UNICEF on the legal status of such countries or territories, of
          their authorities and institutions or of the delimitation of their boundaries.
        </p>
      </Section>

      <Section>
        <H2>6. Privileges and immunities</H2>
        <p>
          Nothing in or relating to these Terms of Use shall be deemed a waiver, express or implied,
          of any of the privileges and immunities of UNICEF, under the Convention on the Privileges
          and Immunities of the United Nations, 1946, or otherwise.
        </p>
      </Section>

      <Section>
        <H2>7. Contact Us</H2>
        <p>
          If you have any questions about this site please use the{' '}
          <A href="https://www.unicef.org/about-unicef/contact-us">feedback form</A>.
        </p>
      </Section>

      <footer>
        <p>Last Updated: 13 September 2021</p>
      </footer>
    </>
  );
}
